/**
 * Magic-link (email-link) sign-in helpers.
 *
 * Why the localStorage dance: Firebase Auth's email link is a single-use OTC.
 * Mail clients, link scanners, and iCloud Private Relay routinely prefetch the
 * URL, which would burn the code before the user could click. Storing the
 * email locally on send means the code is only consumed when *this device*
 * (the one that sent the link) explicitly calls `signInWithEmailLink` with
 * the matching email — prefetchers without our localStorage entry can't
 * complete the call.
 *
 * Firebase Console requirements (one-time):
 *   - Authentication > Sign-in method > Email/Password: enable
 *     "Email link (passwordless sign-in)".
 *   - Authentication > Settings > Authorized domains: confirm `localhost`
 *     (dev) and the production domain are listed.
 */

import {
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  type ActionCodeSettings,
} from 'firebase/auth';
import { getFirebaseAuth } from '../firebase/client';

const STORAGE_KEY = 'mdev3:emailForSignIn';

function makeActionCodeSettings(): ActionCodeSettings {
  // Link redirects back to /login on this same origin. The login page's
  // mount-time effect detects the inbound link and finishes the sign-in.
  return {
    url: `${window.location.origin}/login`,
    handleCodeInApp: true,
  };
}

/** Send a magic-link sign-in email. Caller is responsible for UI feedback. */
export async function sendMagicLink(email: string): Promise<void> {
  const auth = getFirebaseAuth();
  const trimmed = email.trim();
  await sendSignInLinkToEmail(auth, trimmed, makeActionCodeSettings());
  try {
    window.localStorage.setItem(STORAGE_KEY, trimmed);
  } catch {
    // localStorage may be unavailable (private mode, locked-down browser).
    // The user will be prompted for email on landing — flow still works.
  }
}

/** Returns true if the URL contains an email-link sign-in payload. */
export function isMagicLinkUrl(url: string): boolean {
  return isSignInWithEmailLink(getFirebaseAuth(), url);
}

/** Email previously saved when `sendMagicLink` was called on this device. */
export function readPendingEmail(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function clearPendingEmail(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Complete a magic-link sign-in. Throws on auth/expired/invalid. */
export async function completeMagicLinkSignIn(email: string, url: string): Promise<void> {
  const auth = getFirebaseAuth();
  await signInWithEmailLink(auth, email.trim(), url);
  clearPendingEmail();
}
